import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { ReactTabulator, reactFormatter } from 'react-tabulator';
import { Dialog } from 'primereact/dialog';
import { PanelMenu } from 'primereact/panelmenu';
import { Checkbox } from 'primereact/checkbox';
import { MdSearch, MdSearchOff } from 'react-icons/md';
import { tabulatorGet, downloadBlobGet, fetchTextGet } from '../../api/ApiController';
import { DatabaseContext } from '../../redux/DatabaseStore';
import AuthContext from '../../components/AuthContext';
import UtilContext from '../../components/UtilContext';
import { LocaleContext } from '../../components/LocaleContext';
import IntersectionObserverWrapper from '../../components/toolbar/IntersectionObserverWrap';
import { OverflowMenuProvider } from '../../components/toolbar/OverflowMenu';
import PrintOptionsDialog from '../../components/PrintOptionsDialog';
import {
  AddIcon, EditIcon, CopyIcon, DeleteIcon, PrintIcon, SearchIcon, ExportIcon, RefreshIcon,
  VerticalLineIcon, BackToFirstIcon, BackIcon, ForwardIcon, ForwardToLastIcon,
  FavoriteIcon, PriceIcon, CalendarIcon, CalendarOffIcon, EditRowIcon,
} from '../svg/svgIcons';
import { scrollToFirst as scrollToFirstUtil, scrollUp as scrollUpUtil, scrollDown as scrollDownUtil, scrollToLast as scrollToLastUtil } from '../../utils/tabulatorScroll';
import { resetTabulatorMask } from '../../utils/tabulatorResetMask';
import { getEffectiveMask } from '../../utils/effectiveMask';
import {
  cleanSorters, coerceHeaderFilterList, extractSafePaginationParams, createDedupedAjaxRequest,
  safeParse, parseSorters, parseMaskFilters, mergePagedStandardListFiltersAndSorters,
  sanitizeTabulatorLayoutAttributes, TABULATOR_GROUP_A_FLAT_REMOTE_LAYOUT_SANITIZE_OPTIONS,
} from '../../utils/tabulatorProgressiveLoading';
import { dateUserHeaderFilter } from '../../utils/dateUserHeaderFilter';
import { inputOnEnterHeaderFilter } from '../../utils/inputOnEnterHeaderFilter';
import { tabulatorColumnMinWidthFromTitle, tabulatorDateUserColumnMinWidth } from '../../utils/tabulatorColumnMinWidth';
import {
  TABULATOR_AUDIT_MIN_ROW_HEIGHT,
  TABULATOR_AUDIT_PROFILES,
  createAuditDateUserFormatter,
  createAuditDateUserHeaderFilterFunc,
} from '../../utils/tabulatorAuditColumns';
import { buildExportQueryStringFromParams, openPrintHtmlWindow, triggerBlobDownload } from '../../utils/serviceTaskExport';
import { xlsxDownloadFilename } from '../../utils/exportExcelNaming';
import { pinMeasureGroupLeadingColumns, measureGroupKey } from '../../utils/measureGroupListMasks';
import {
  MEASURE_GROUPS_UPDATED_EVENT,
  SESSION_INVALIDATING,
  classifyMeasureGroupListMutation,
  enrichMeasureGroupRows,
  remapMeasureGroupAuditSource,
} from '../../utils/measureGroupListSync';
import '../../assets/css/style.css';
import '../../assets/css/iconsmenu.css';
import { applyTabulatorRuntimeI18n } from '../../utils/tabulatorRuntimeI18n';
const MEASURE_GROUP_PROGRESSIVE_LIST_COLUMN_I18N_KEYS = Object.freeze({
  "order_number": "MeasureGroupList.Column.OrderNumber",
  "contractor.defined_id": "MeasureGroupList.Column.ContractorDefinedId",
  "count": "MeasureGroupList.Column.Count",
  "group_created_at": "MeasureGroupList.Column.CreatedAt",
  "group_updated_at": "MeasureGroupList.Column.UpdatedAt",
});



const DEFAULT_COLUMNS_VISIBLE = Object.freeze({
  order_number: true,
  'contractor.defined_id': true,
  count: true,
  group_created_at: true,
  group_updated_at: true,
});

function mergeVisibility(saved) {
  const merged = { ...DEFAULT_COLUMNS_VISIBLE };
  if (saved && typeof saved === 'object') {
    Object.keys(DEFAULT_COLUMNS_VISIBLE).forEach((k) => {
      if (Object.prototype.hasOwnProperty.call(saved, k)) merged[k] = saved[k] !== false;
    });
  }
  if (!Object.values(merged).some(Boolean)) return { ...DEFAULT_COLUMNS_VISIBLE };
  return merged;
}

function applyDataColumnVisibilityToTable(table, visibilityMap) {
  if (!table) return;
  for (const f of Object.keys(DEFAULT_COLUMNS_VISIBLE)) {
    if (visibilityMap[f] === false) table.hideColumn?.(f);
    else table.showColumn?.(f);
  }
  setTimeout(() => table.redraw?.(true), 0);
}

function headerFiltersEquivalent(a, b) {
  const norm = (list) => coerceHeaderFilterList(list)
    .filter((filter) => filter?.field && String(filter.value ?? '').trim() !== '')
    .map((filter) => `${filter.field}\0${String(filter.value).trim()}`)
    .sort()
    .join('\n');
  return norm(a) === norm(b);
}

function sortersEquivalent(a, b) {
  const norm = (list) => cleanSorters(list || [])
    .map((sorter) => `${sorter.field}\0${sorter.dir || 'asc'}`)
    .join('\n');
  return norm(a) === norm(b);
}

function hasLeadingLayout(attributes) {
  if (!Array.isArray(attributes)) return false;
  return attributes.some((node) => node && typeof node === 'object' && (node.field === 'editor' || node.field === 'rowSelection'));
}

export default function MeasureGroupProgressiveList({ mask }) {
  const [state, dispatch] = useContext(DatabaseContext);
  const { user } = useContext(AuthContext);
  const { t, locale } = useContext(LocaleContext);
  const {
    setUserMaskFilter, openFavoriteDialog, toast, setFastView, getDateFormat,
    getUserColor, getUserInitials, openChosenNotificationPreferences, setDialogText, setVisible,
  } = useContext(UtilContext);

  const dateFmt = getDateFormat?.(state?.module_settings) ?? 'dd.MM.yyyy';
  const dateTimeFmt = `${dateFmt} HH:mm`;

  const pageSizePc = useMemo(() => {
    const v = (state.module_settings || []).find((s) => s.module === 'global' && s.key === 'page_size_pc')?.value;
    const n = parseInt(v, 10);
    return (Number.isFinite(n) && n > 0) ? n : 50;
  }, [state.module_settings]);

  const canAdd = !!user?.permissions?.includes(mask.permissions.add);
  const [insertIcon] = useState(!canAdd);
  const [editIcon, setEditIcon] = useState(true);
  const [copyIcon, setCopyIcon] = useState(true);
  const [deleteIcon] = useState(true); // always disabled — do not activate delete
  const [visibleSearch, setVisibleSearch] = useState(false);
  const [searchIconColor, setSearchIconColor] = useState('');
  const [searchValue, setSearchValue] = useState('');
  const [visibleCalendar, setVisibleCalendar] = useState(false);
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [calendarActive, setCalendarActive] = useState(false);
  const [markUnmark, setMarkUnmarked] = useState(true);
  const [columnsVisible, setColumnsVisible] = useState({ ...DEFAULT_COLUMNS_VISIBLE });
  const [maskVisible, setMaskVisible] = useState(false);
  const [printChoiceVisible, setPrintChoiceVisible] = useState(false);
  const [printOrientationChoice, setPrintOrientationChoice] = useState('landscape');
  const [printLayoutChoice, setPrintLayoutChoice] = useState('branded');
  const [tableSessionKey, setTableSessionKey] = useState(0);

  const ref = useRef(null);
  const overlayRef = useRef(null);
  const inFlightRef = useRef({ sig: null, promise: null });
  const completedCacheRef = useRef(new Map());
  const tableBuiltRef = useRef(false);
  const lastResponseRef = useRef(null);
  const isRestoringRef = useRef(false);
  const userInteractedRef = useRef(false);
  const savedHScrollRef = useRef({ left: 0, pending: false });
  const lastContainerHeightRef = useRef(0);
  const submittedSearchValueRef = useRef('');
  const submittedFromDateRef = useRef('');
  const submittedToDateRef = useRef('');
  const pendingInvalidationRef = useRef(false);
  const listMountedRef = useRef(true);
  const restartQueuedRef = useRef(false);
  const ajaxGenerationRef = useRef(0);
  const confirmedQueryRef = useRef({ filters: [], sorters: [] });
  const sessionQueryActiveRef = useRef(false);
  const tableContainerRef = useRef(null);

  const getTable = useCallback(() => ref.current?.table ?? ref.current, []);
  const isTableVisible = useCallback(() => {
    const el = tableContainerRef.current;
    if (el && el.offsetParent != null && el.clientHeight > 0) return true;
    const table = getTable();
    const tel = table?.getElement?.() ?? table?.element;
    return !!(tel && tel.offsetParent != null && tel.clientHeight > 0);
  }, [getTable]);

  const snapshotConfirmedQuery = useCallback(() => {
    const table = getTable();
    confirmedQueryRef.current = {
      filters: coerceHeaderFilterList(table?.getHeaderFilters?.()),
      sorters: cleanSorters(table?.getSorters?.() || []),
    };
  }, [getTable]);

  const restartFullProgressiveSession = useCallback(() => {
    if (!listMountedRef.current || restartQueuedRef.current) return;
    snapshotConfirmedQuery();
    sessionQueryActiveRef.current = true;
    ajaxGenerationRef.current += 1;
    pendingInvalidationRef.current = false;
    restartQueuedRef.current = true;
    completedCacheRef.current.clear();
    lastResponseRef.current = null;
    inFlightRef.current = { sig: null, promise: null };
    tableBuiltRef.current = false;
    isRestoringRef.current = true;
    const table = getTable();
    if (typeof table?.destroy === 'function') table.destroy();
    ref.current = null;
    setTableSessionKey((key) => key + 1);
  }, [getTable, snapshotConfirmedQuery]);

  const requestFullProgressiveSessionRestart = useCallback(() => {
    if (!listMountedRef.current) return;
    if (!isTableVisible()) {
      pendingInvalidationRef.current = true;
      return;
    }
    restartFullProgressiveSession();
  }, [isTableVisible, restartFullProgressiveSession]);

  const captureHScroll = useCallback(() => {
    const table = getTable();
    if (!table) return;
    const holder = table.rowManager?.element;
    if (!holder) return;
    savedHScrollRef.current = { left: holder.scrollLeft, pending: true };
  }, [getTable]);

  const restoreHScroll = useCallback(() => {
    if (!savedHScrollRef.current.pending) return;
    const table = getTable();
    if (!table) return;
    const holder = table.rowManager?.element;
    const header = table.columnManager?.contentsElement;
    if (!holder) return;
    const left = savedHScrollRef.current.left;
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        holder.scrollLeft = left;
        if (header) header.scrollLeft = left;
        savedHScrollRef.current.pending = false;
      });
    });
  }, [getTable]);

  useEffect(() => {
    listMountedRef.current = true;
    return () => { listMountedRef.current = false; };
  }, []);

  useEffect(() => {
    const el = tableContainerRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(() => {
      const h = el.clientHeight;
      const visible = h > 0 || isTableVisible();
      if (visible) {
        const wasHidden = lastContainerHeightRef.current <= 0;
        lastContainerHeightRef.current = Math.max(h, 1);
        if (pendingInvalidationRef.current) {
          requestAnimationFrame(() => restartFullProgressiveSession());
        } else if (wasHidden) {
          requestAnimationFrame(() => getTable()?.redraw?.(true));
        }
      } else {
        lastContainerHeightRef.current = 0;
      }
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, [getTable, isTableVisible, restartFullProgressiveSession]);

  useEffect(() => {
    const onUpdated = (event) => {
      const decision = classifyMeasureGroupListMutation(event, { measureType: mask.measureType });
      if (decision == null) return;
      if (decision === SESSION_INVALIDATING) {
        requestFullProgressiveSessionRestart();
      }
    };
    window.addEventListener(MEASURE_GROUPS_UPDATED_EVENT, onUpdated);
    return () => window.removeEventListener(MEASURE_GROUPS_UPDATED_EVENT, onUpdated);
  }, [mask.measureType, requestFullProgressiveSessionRestart]);

  function onEditRow(record) {
    dispatch({
      type: 'create_new_tab',
      name: mask.editTabName,
      translationKey: mask.editTabKey,
      legacyLabel: mask.editTabName,
      page: mask.detailPage,
      props: { measure: record, name: mask.measureType, type: 'edit', not_last_used: true },
      mask: mask.detailMask,
    });
  }

  function EditRowFormatter(props) {
    const rowData = props.cell.getRow().getData();
    return (
      <div className="flex items-center justify-center w-full h-full">
        <button type="button" onClick={() => onEditRow(rowData)}><EditRowIcon /></button>
      </div>
    );
  }

  const textHeaderFilter = useCallback((headerValue) => {
    if (!headerValue || String(headerValue).trim() === '') return true;
    return true;
  }, []);

  const auditHeaderFilterFunc = useMemo(() => createAuditDateUserHeaderFilterFunc({
    profile: TABULATOR_AUDIT_PROFILES.createdByUpdatedBy,
    dateFormat: dateFmt,
    dateTimeFormat: dateTimeFmt,
    auditSourceFn: remapMeasureGroupAuditSource,
    remotePassthrough: true,
  }), [dateFmt, dateTimeFmt]);

  // dateFormat must be date-only: createAuditDateUserFormatter calls formatDate(..., dateFormat, true)
  // which appends " HH:mm". Passing dateTimeFmt caused duplicated time (e.g. "15:03 15:03").
  const CreatedAuditFormatter = useMemo(() => createAuditDateUserFormatter({
    profile: TABULATOR_AUDIT_PROFILES.createdByUpdatedBy,
    kind: 'created',
    dateFormat: dateFmt,
    getUserColor,
    getUserInitials,
    auditSourceFn: remapMeasureGroupAuditSource,
  }), [dateFmt, getUserColor, getUserInitials]);

  const UpdatedAuditFormatter = useMemo(() => createAuditDateUserFormatter({
    profile: TABULATOR_AUDIT_PROFILES.createdByUpdatedBy,
    kind: 'updated',
    dateFormat: dateFmt,
    getUserColor,
    getUserInitials,
    auditSourceFn: remapMeasureGroupAuditSource,
  }), [dateFmt, getUserColor, getUserInitials]);

  const orderTitle = t('MeasureGroupList.Column.OrderNumber');
  const contractorTitle = t('MeasureGroupList.Column.ContractorDefinedId');
  const countTitle = t('MeasureGroupList.Column.Count');
  const createdTitle = t('MeasureGroupList.Column.CreatedAt');
  const updatedTitle = t('MeasureGroupList.Column.UpdatedAt');

  const columns = useMemo(() => [
    { field: 'rowSelection', formatter: 'rowSelection', titleFormatter: 'rowSelection', hozAlign: 'center', width: 44, minWidth: 44, headerSort: false, resizable: false, frozen: true, print: false, download: false, headerFilter: false },
    { title: '', field: 'editor', hozAlign: 'center', width: 44, minWidth: 44, headerSort: false, formatter: reactFormatter(<EditRowFormatter />), resizable: false, frozen: true, print: false, download: false, headerFilter: false },
    { title: orderTitle, field: 'order_number', headerFilter: inputOnEnterHeaderFilter, headerFilterLiveFilter: false, headerFilterFunc: textHeaderFilter, minWidth: tabulatorColumnMinWidthFromTitle(orderTitle), widthGrow: 1, headerSort: true },
    { title: contractorTitle, field: 'contractor.defined_id', headerFilter: inputOnEnterHeaderFilter, headerFilterLiveFilter: false, headerFilterFunc: textHeaderFilter, minWidth: tabulatorColumnMinWidthFromTitle(contractorTitle), widthGrow: 1, headerSort: true },
    { title: countTitle, field: 'count', headerFilter: inputOnEnterHeaderFilter, headerFilterLiveFilter: false, headerFilterFunc: textHeaderFilter, minWidth: tabulatorColumnMinWidthFromTitle(countTitle), widthGrow: 1, headerSort: true },
    {
      title: createdTitle,
      field: 'group_created_at',
      minWidth: tabulatorDateUserColumnMinWidth(createdTitle),
      formatter: reactFormatter(<CreatedAuditFormatter />),
      headerFilter: dateUserHeaderFilter,
      headerFilterLiveFilter: false,
      headerFilterParams: { dateFormat: dateFmt, dateTimeFormat: dateTimeFmt },
      headerFilterFunc: auditHeaderFilterFunc,
      headerFilterFuncParams: { name: 'created_at' },
      hozAlign: 'left',
      sorter: 'datetime',
      sorterParams: { format: 'iso' },
      headerSort: true,
    },
    {
      title: updatedTitle,
      field: 'group_updated_at',
      minWidth: tabulatorDateUserColumnMinWidth(updatedTitle),
      formatter: reactFormatter(<UpdatedAuditFormatter />),
      headerFilter: dateUserHeaderFilter,
      headerFilterLiveFilter: false,
      headerFilterParams: { dateFormat: dateFmt, dateTimeFormat: dateTimeFmt },
      headerFilterFunc: auditHeaderFilterFunc,
      headerFilterFuncParams: { name: 'updated_at' },
      hozAlign: 'left',
      sorter: 'datetime',
      sorterParams: { format: 'iso' },
      headerSort: true,
    },
  // Formatters are stable per dateFmt/user helpers; listed deps are titles + filter funcs.
  // eslint-disable-next-line react-hooks/exhaustive-deps -- Created/UpdatedAuditFormatter intentionally omitted (recreated each render via factory; columns rebuild on title/filter deps)
  ], [orderTitle, contractorTitle, countTitle, createdTitle, updatedTitle, dateFmt, dateTimeFmt, textHeaderFilter, auditHeaderFilterFunc]);

  const buildRequestParams = useCallback((paramsFromTabulator = {}) => {
    const extra = { ...extractSafePaginationParams(paramsFromTabulator) };
    if (extra.size == null) extra.size = pageSizePc;
    if (submittedFromDateRef.current && submittedToDateRef.current) {
      extra.from_date = submittedFromDateRef.current;
      extra.to_date = submittedToDateRef.current;
    }
    if (sessionQueryActiveRef.current) {
      coerceHeaderFilterList(confirmedQueryRef.current.filters).forEach((filter) => {
        if (!filter?.field) return;
        const value = filter.value;
        if (value == null || String(value).trim() === '') return;
        extra[`filter_${filter.field}`] = value;
      });
      const confirmedSorters = cleanSorters(confirmedQueryRef.current.sorters || []);
      if (confirmedSorters.length > 0) extra.sorters = JSON.stringify(confirmedSorters);
    } else {
      mergePagedStandardListFiltersAndSorters(extra, {
        getTable: tableBuiltRef.current ? getTable : () => null,
        tableBuiltForParams: tableBuiltRef.current,
        userMaskFilters: state.user_mask_filters,
        maskPage: mask.pageListKey,
        filterKeyStyle: 'literal_field',
      });
    }
    const search = String(submittedSearchValueRef.current ?? '').trim();
    if (search !== '') extra.toolbar_search = search;
    return extra;
  }, [getTable, state.user_mask_filters, pageSizePc, mask.pageListKey]);

  
  useEffect(() => {
    const table = typeof getTable === 'function' ? getTable() : null;
    if (!table?.getColumns) return;
    applyTabulatorRuntimeI18n(table, t, {
      columnTitleKeys: MEASURE_GROUP_PROGRESSIVE_LIST_COLUMN_I18N_KEYS,
      placeholder: typeof t === 'function' ? t('Common.NoData') : undefined,
      reformatRows: true,
    });
  }, [locale, t, getTable]);

const buildRequestParamsRef = useRef(buildRequestParams);
  buildRequestParamsRef.current = buildRequestParams;
  const getBuildRequestParams = useCallback((paramsFromTabulator) => buildRequestParamsRef.current(paramsFromTabulator), []);

  const getPrintColumnsOrder = useCallback(() => {
    const table = getTable();
    const allowed = Object.keys(DEFAULT_COLUMNS_VISIBLE);
    const fallback = () => allowed.filter((f) => columnsVisible[f] !== false);
    if (!table?.getColumns) return fallback();
    const allowedSet = new Set(allowed);
    const out = [];
    const seen = new Set();
    try {
      table.getColumns().forEach((col) => {
        const field = col.getField?.() ?? col.getDefinition?.()?.field;
        if (!field || !allowedSet.has(field)) return;
        if (columnsVisible[field] === false) return;
        if (seen.has(field)) return;
        seen.add(field);
        out.push(field);
      });
    } catch (_) {
      return fallback();
    }
    return out.length > 0 ? out : fallback();
  }, [getTable, columnsVisible]);

  const buildExportQueryString = useCallback((opts = {}) => {
    const extra = {};
    mergePagedStandardListFiltersAndSorters(extra, {
      getTable: tableBuiltRef.current ? getTable : () => null,
      tableBuiltForParams: tableBuiltRef.current,
      userMaskFilters: state.user_mask_filters,
      maskPage: mask.pageListKey,
      filterKeyStyle: 'literal_field',
    });
    const search = String(submittedSearchValueRef.current ?? '').trim();
    if (search !== '') extra.toolbar_search = search;
    if (submittedFromDateRef.current && submittedToDateRef.current) {
      extra.from_date = submittedFromDateRef.current;
      extra.to_date = submittedToDateRef.current;
    }
    return buildExportQueryStringFromParams(extra, opts);
  }, [getTable, state.user_mask_filters, mask.pageListKey]);

  const runPrint = useCallback(async () => {
    try {
      setPrintChoiceVisible(false);
      setDialogText?.(t('Common.PreparingPrint'));
      setVisible?.(true);
      const suffix = buildExportQueryString({
        includePrintColumns: true,
        printLayout: printLayoutChoice,
        printOrientation: printOrientationChoice,
        printColumns: getPrintColumnsOrder(),
      });
      const html = await fetchTextGet(`${mask.printUrl}${suffix}`);
      if (!openPrintHtmlWindow(html)) {
        toast?.current?.show({ severity: 'warn', summary: t('Common.Print'), detail: t('Common.EnablePopups'), life: 4000 });
      }
    } catch (e) {
      toast?.current?.show({ severity: 'error', summary: t('Common.Print'), detail: e?.message || t('Common.PrintFailed'), life: 4500 });
    } finally {
      setVisible?.(false);
    }
  }, [buildExportQueryString, getPrintColumnsOrder, mask.printUrl, printLayoutChoice, printOrientationChoice, setDialogText, setVisible, t, toast]);

  const exportXlsx = useCallback(async () => {
    try {
      setDialogText?.(t('Common.PreparingExport'));
      setVisible?.(true);
      const suffix = buildExportQueryString({
        includePrintColumns: true,
        printColumns: getPrintColumnsOrder(),
      });
      const defaultXlsx = xlsxDownloadFilename(t(mask.exportTitleKey));
      const { blob, filename } = await downloadBlobGet(`${mask.xlsxUrl}${suffix}`, defaultXlsx);
      triggerBlobDownload(blob, filename || defaultXlsx);
    } catch (e) {
      toast?.current?.show({ severity: 'error', summary: t('Common.Export'), detail: e?.message || t('Common.ExportFailed'), life: 4500 });
    } finally {
      setVisible?.(false);
    }
  }, [buildExportQueryString, getPrintColumnsOrder, mask.exportTitleKey, mask.xlsxUrl, setDialogText, setVisible, t, toast]);

  const restoreHeaderFilters = useCallback((table, rawFilters) => {
    const filterEntries = Array.isArray(rawFilters)
      ? rawFilters
      : (rawFilters && typeof rawFilters === 'object'
        ? Object.entries(rawFilters).map(([field, value]) => ({ field, value }))
        : []);
    filterEntries.forEach((filter) => {
      if (!filter?.field) return;
      const value = filter.value;
      if (value == null || value === '') return;
      const def = table.getColumn?.(filter.field)?.getDefinition?.();
      if (!def?.headerFilter) return;
      table.setHeaderFilterValue?.(filter.field, value, true);
    });
  }, []);

  const searchTable = () => {
    const normalized = String(searchValue ?? '').trim();
    submittedSearchValueRef.current = normalized;
    setSearchIconColor(normalized === '' ? '' : 'active');
    captureHScroll();
    requestFullProgressiveSessionRestart();
    setVisibleSearch(false);
  };

  const clearSearch = () => {
    setSearchIconColor('');
    setSearchValue('');
    submittedSearchValueRef.current = '';
    captureHScroll();
    requestFullProgressiveSessionRestart();
  };

  const searchByDate = () => {
    submittedFromDateRef.current = fromDate || '';
    submittedToDateRef.current = toDate || '';
    setCalendarActive(!!(fromDate && toDate));
    setVisibleCalendar(false);
    captureHScroll();
    requestFullProgressiveSessionRestart();
  };

  const clearCalendar = () => {
    setFromDate('');
    setToDate('');
    submittedFromDateRef.current = '';
    submittedToDateRef.current = '';
    setCalendarActive(false);
    captureHScroll();
    requestFullProgressiveSessionRestart();
  };

  const onSelectRow = (data) => {
    setEditIcon(true);
    setCopyIcon(true);
    if (!data || data.length === 0) return;
    const row = data[0];
    const isOwner = Number(row?.contractor_id) === Number(user?.employee?.id);
    if (user?.permissions?.includes(mask.permissions.add) || isOwner) setCopyIcon(false);
    if (user?.permissions?.includes(mask.permissions.edit) || isOwner) setEditIcon(false);
  };

  const scrollToFirst = () => scrollToFirstUtil(getTable());
  const scrollUp = () => scrollUpUtil(getTable());
  const scrollDown = () => scrollDownUtil(getTable());
  const scrollToLast = () => scrollToLastUtil(getTable());

  function addNewRecord() {
    dispatch({
      type: 'create_new_tab',
      name: mask.addTabName,
      translationKey: mask.addTabKey,
      legacyLabel: mask.addTabName,
      page: mask.addPage,
      props: { measure: null, name: mask.measureType, type: 'add', not_last_used: true },
      mask: mask.addMask,
    });
  }

  function copyRecord() {
    const selected = getTable()?.getSelectedData?.() ?? [];
    if (selected.length === 0) return;
    dispatch({
      type: 'create_new_tab',
      name: mask.addTabName,
      translationKey: mask.addTabKey,
      legacyLabel: mask.addTabName,
      page: mask.addPage,
      props: { measure: selected[0], name: mask.measureType, type: 'add', not_last_used: true },
      mask: mask.addMask,
    });
  }

  function editSelected() {
    const sel = getTable()?.getSelectedData?.()?.[0];
    if (sel) onEditRow(sel);
  }

  function setMaskColumn(item, value) {
    if (!Object.prototype.hasOwnProperty.call(DEFAULT_COLUMNS_VISIBLE, item)) return;
    const next = { ...DEFAULT_COLUMNS_VISIBLE, ...columnsVisible, [item]: value };
    if (!Object.values(next).some(Boolean)) return;
    applyDataColumnVisibilityToTable(getTable(), next);
    setColumnsVisible(next);
  }

  function changeMarkings(value) {
    const next = { ...DEFAULT_COLUMNS_VISIBLE };
    Object.keys(next).forEach((k) => { next[k] = value; });
    applyDataColumnVisibilityToTable(getTable(), next);
    setColumnsVisible(next);
  }

  const itemRenderer = (item, options) => (
    <button
      type="button"
      className="flex align-items-center px-3 py-2 cursor-pointer items-center bg-[#333] text-white w-[180px] border-0 text-left"
      onClick={options.onClick}
    >
      <span className={`${item.icon} text-primary`} />
      <span className={`mx-2 ${item.items && 'font-semibold'}`}>{item.label}</span>
    </button>
  );

  const [expandedKeys, setExpandedKeys] = useState({});
  const handleClickOutside = (event) => {
    if (overlayRef.current && !overlayRef.current.contains(event.target)) setExpandedKeys({});
  };
  useEffect(() => {
    document.addEventListener('click', handleClickOutside, true);
    return () => document.removeEventListener('click', handleClickOutside, true);
  }, []);

  const resetMask = () => resetTabulatorMask({
    page: mask.pageListKey,
    defaultColumnsVisible: DEFAULT_COLUMNS_VISIBLE,
    ref,
    columns,
    setColumnsVisible,
    onAfterReset: () => requestFullProgressiveSessionRestart(),
  });

  const items = [
    {
      key: '0',
      label: t('Common.Actions'),
      icon: 'pi pi-sliders-v',
      template: itemRenderer,
      items: [
        { key: '0_1', label: t('Common.SetFilter'), icon: 'pi pi-filter', command: () => { userInteractedRef.current = true; setUserMaskFilter(mask.pageListKey, 'set_filters', null, getTable()?.getHeaderFilters?.() ?? [], null); requestFullProgressiveSessionRestart(); }, template: itemRenderer },
        { key: '0_2', label: t('Common.ClearFilter'), icon: 'pi pi-filter-slash', command: () => { userInteractedRef.current = true; setUserMaskFilter(mask.pageListKey, 'set_filters', null, [], null); getTable()?.clearHeaderFilter?.(); requestFullProgressiveSessionRestart(); }, template: itemRenderer },
        { key: '0_3', label: t('Common.SetSorting'), icon: 'pi pi-sort-alt', command: () => { userInteractedRef.current = true; setUserMaskFilter(mask.pageListKey, 'set_sorters', null, null, null, cleanSorters(getTable()?.getSorters?.() ?? [])); OverflowMenuProvider.setVisible(null); setExpandedKeys({}); requestFullProgressiveSessionRestart(); }, template: itemRenderer },
        { key: '0_4', label: t('Common.ClearSorting'), icon: 'pi pi-sort-alt-slash', command: () => { userInteractedRef.current = true; setUserMaskFilter(mask.pageListKey, 'set_sorters', null, null, null, []); getTable()?.setSort?.([]); OverflowMenuProvider.setVisible(null); setExpandedKeys({}); requestFullProgressiveSessionRestart(); }, template: itemRenderer },
        { key: '0_5', label: t('Common.SetMask'), icon: 'pi pi-server', command: () => { setMaskVisible(true); OverflowMenuProvider.setVisible(null); setExpandedKeys({}); }, template: itemRenderer },
        { key: '0_5b', label: t('Common.ResetUserMask'), icon: 'pi pi-refresh', command: () => { resetMask(); OverflowMenuProvider.setVisible(null); setExpandedKeys({}); }, template: itemRenderer },
        { key: '0_6', label: t('Common.Notifications'), icon: 'pi pi-envelope', command: () => { openChosenNotificationPreferences(mask.notificationPage); OverflowMenuProvider.setVisible(null); }, template: itemRenderer },
      ],
    },
  ];

  return (
    <div className="h-full min-h-0 flex flex-col overflow-hidden">
      <div className="w-full h-10 flex flex-row bg-[#333] shrink-0">
        <IntersectionObserverWrapper>
          <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center" data-targetid="add"><button type="button" className="disabled:opacity-25" disabled={insertIcon} onClick={() => addNewRecord()}><AddIcon /></button></div>
          <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center" data-targetid="edit"><button type="button" className="disabled:opacity-25" disabled={editIcon} onClick={() => editSelected()}><EditIcon /></button></div>
          <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center" data-targetid="copy"><button type="button" className="disabled:opacity-25" disabled={copyIcon} onClick={() => copyRecord()}><CopyIcon /></button></div>
          <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center" data-targetid="delete"><button type="button" className="disabled:opacity-25" disabled={deleteIcon} title={t('MeasureGroupList.Toolbar.DeleteDisabled')}><DeleteIcon /></button></div>
          <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center" data-targetid="print"><button type="button" onClick={() => setPrintChoiceVisible(true)} title={t('Common.Print')}><PrintIcon /></button></div>
          <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center" data-targetid="export"><button type="button" onClick={() => exportXlsx()} title={t('Common.ExportToExcel')}><ExportIcon /></button></div>
          <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center" data-targetid="search">
            <button type="button" title={searchIconColor !== 'active' ? t('Common.Search') : t('ServiceTask.CancelSearch')} onClick={searchIconColor !== 'active' ? () => setVisibleSearch(true) : clearSearch}>
              {searchIconColor === '' ? <MdSearch fill="#fff" className="w-[28px] h-[28px]" aria-hidden="true" /> : <MdSearchOff fill="#fff" className="w-[28px] h-[28px]" aria-hidden="true" />}
            </button>
          </div>
          <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center align-center" data-targetid="calendar">
            <button type="button" onClick={!calendarActive ? () => setVisibleCalendar(true) : clearCalendar} title={t('MeasureGroupList.Toolbar.Calendar')}>
              {calendarActive ? <CalendarOffIcon /> : <CalendarIcon />}
            </button>
          </div>
          <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center" data-targetid="refresh"><button type="button" onClick={() => requestFullProgressiveSessionRestart()} title={t('Common.Refresh')}><RefreshIcon /></button></div>
          <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center" data-targetid="vert"><VerticalLineIcon /></div>
          <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center" data-targetid="first"><button type="button" onClick={() => scrollToFirst()}><BackToFirstIcon /></button></div>
          <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center" data-targetid="back"><button type="button" onClick={() => scrollUp()}><BackIcon /></button></div>
          <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center" data-targetid="forward"><button type="button" onClick={() => scrollDown()}><ForwardIcon /></button></div>
          <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center" data-targetid="last"><button type="button" onClick={() => scrollToLast()}><ForwardToLastIcon /></button></div>
          <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center" data-targetid="vert2"><VerticalLineIcon /></div>
          <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center" data-targetid="favorite"><button type="button" onClick={() => openFavoriteDialog(mask.favoriteTitle, mask.pageListKey)}><FavoriteIcon /></button></div>
          <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center" data-targetid="fast"><button type="button" onClick={() => setFastView(mask.pageListKey, columnsVisible, columns)}><PriceIcon /></button></div>
          <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center" data-targetid="vert3"><VerticalLineIcon /></div>
          <div className="card flex justify-content-center z-[100]" data-targetid="actions" ref={overlayRef}>
            <PanelMenu model={items} expandedKeys={expandedKeys} onExpandedKeysChange={setExpandedKeys} className="w-full md:w-20rem" />
          </div>
        </IntersectionObserverWrapper>
      </div>
      <div className="px-1 bg-white rounded-md shadow-md flex-1 min-h-0 overflow-hidden">
        <div ref={tableContainerRef} className="flex-1 min-h-0 overflow-hidden">
          <ReactTabulator
            key={tableSessionKey}
            className="h-[calc(100vh_-_193px)]"
            onRef={(r) => { ref.current = r?.current ?? r; }}
            columns={columns}
            events={{
              rowSelectionChanged: onSelectRow,
              tableBuilt: () => {
                isRestoringRef.current = true;
                userInteractedRef.current = false;
                const table = getTable();
                const restartRestore = sessionQueryActiveRef.current;
                if (!table) {
                  tableBuiltRef.current = true;
                  restartQueuedRef.current = false;
                  snapshotConfirmedQuery();
                  setTimeout(() => {
                    isRestoringRef.current = false;
                    sessionQueryActiveRef.current = false;
                  }, 0);
                  return;
                }
                const effective = getEffectiveMask(mask.pageListKey, state);
                if (effective?.type === 'user' && effective.userMask) {
                  const userMask = effective.userMask;
                  try {
                    table.blockRedraw?.();
                    const attributes = pinMeasureGroupLeadingColumns(
                      sanitizeTabulatorLayoutAttributes(safeParse(userMask.attributes, []), TABULATOR_GROUP_A_FLAT_REMOTE_LAYOUT_SANITIZE_OPTIONS)
                    );
                    if (hasLeadingLayout(attributes) && attributes.length > 1) {
                      table.setColumnLayout?.(attributes);
                    }
                    if (restartRestore) {
                      restoreHeaderFilters(table, confirmedQueryRef.current.filters);
                    } else {
                      restoreHeaderFilters(table, parseMaskFilters(userMask.filters));
                      const sorters = parseSorters(userMask.sorters).map((s) => ({
                        column: s.column ?? s.field,
                        dir: s.dir === 'desc' ? 'desc' : 'asc',
                      }));
                      if (sorters.length > 0) table.setSort?.(sorters);
                    }
                    const jsonColumns = safeParse(userMask.columns, {});
                    if (jsonColumns && typeof jsonColumns === 'object' && !Array.isArray(jsonColumns)) {
                      const merged = mergeVisibility(jsonColumns);
                      setColumnsVisible(merged);
                      applyDataColumnVisibilityToTable(table, merged);
                    }
                  } finally {
                    table.restoreRedraw?.(true);
                  }
                } else if (effective?.type === 'standard' && effective.standardMask) {
                  try {
                    table.blockRedraw?.();
                    const attributes = pinMeasureGroupLeadingColumns(
                      sanitizeTabulatorLayoutAttributes(safeParse(effective.standardMask.attributes, []), TABULATOR_GROUP_A_FLAT_REMOTE_LAYOUT_SANITIZE_OPTIONS)
                    );
                    const jsonColumns = safeParse(effective.standardMask.columns, {});
                    if (hasLeadingLayout(attributes) && attributes.length > 1) {
                      try { table.setColumnLayout?.(attributes); } catch (_) { /* ignore */ }
                    }
                    if (jsonColumns && typeof jsonColumns === 'object') {
                      const merged = mergeVisibility(jsonColumns);
                      setColumnsVisible(merged);
                      applyDataColumnVisibilityToTable(table, merged);
                    }
                    if (restartRestore) restoreHeaderFilters(table, confirmedQueryRef.current.filters);
                  } finally {
                    table.restoreRedraw?.(true);
                  }
                } else {
                  applyDataColumnVisibilityToTable(table, DEFAULT_COLUMNS_VISIBLE);
                  if (restartRestore) restoreHeaderFilters(table, confirmedQueryRef.current.filters);
                }
                tableBuiltRef.current = true;
                restartQueuedRef.current = false;
                snapshotConfirmedQuery();
                setTimeout(() => {
                  isRestoringRef.current = false;
                  sessionQueryActiveRef.current = false;
                }, 0);
              },
              headerFilterChanged: () => {
                if (isRestoringRef.current) return;
                const next = coerceHeaderFilterList(getTable()?.getHeaderFilters?.());
                if (headerFiltersEquivalent(next, confirmedQueryRef.current.filters)) return;
                userInteractedRef.current = true;
                requestFullProgressiveSessionRestart();
              },
              sortChanged: () => {
                if (isRestoringRef.current) return;
                const next = cleanSorters(getTable()?.getSorters?.() || []);
                if (sortersEquivalent(next, confirmedQueryRef.current.sorters)) {
                  userInteractedRef.current = true;
                  return;
                }
                userInteractedRef.current = true;
                requestFullProgressiveSessionRestart();
              },
              dataSorted: () => {
                if (!isRestoringRef.current) userInteractedRef.current = true;
              },
              columnMoved: () => {
                if (!userInteractedRef.current || isRestoringRef.current) return;
                setUserMaskFilter(mask.pageListKey, 'column_move', null, null, getTable()?.getColumnLayout?.());
              },
              columnResized: () => {
                if (!userInteractedRef.current || isRestoringRef.current) return;
                setUserMaskFilter(mask.pageListKey, 'column_resized', null, null, getTable()?.getColumnLayout?.());
              },
              renderComplete: () => restoreHScroll(),
            }}
            options={{
              index: 'group_key',
              renderHorizontal: 'virtual',
              layout: 'fitColumns',
              autoResize: true,
              rowHeight: TABULATOR_AUDIT_MIN_ROW_HEIGHT,
              movableColumns: true,
              headerSortTristate: true,
              ...(cleanSorters(confirmedQueryRef.current.sorters || []).length > 0
                ? {
                  initialSort: cleanSorters(confirmedQueryRef.current.sorters)
                    .filter((sorter) => sorter.field !== 'created_at')
                    .map((sorter) => ({
                      column: sorter.field,
                      dir: sorter.dir,
                    })),
                }
                // No visible created_at/Dátum column — omit Tabulator initialSort; BE default remains MAX(created_at) desc for date-dialog compat.
                : {}),
              ajaxURL: mask.pagedUrl,
              ajaxParams: () => ({}),
              ajaxRequestFunc: (url, config, params) => {
                const generation = ajaxGenerationRef.current;
                return createDedupedAjaxRequest(getBuildRequestParams, tabulatorGet, inFlightRef, completedCacheRef, isRestoringRef, lastResponseRef)(url, config, params).then((data) => {
                  if (generation !== ajaxGenerationRef.current) {
                    return { data: [], last_page: data?.last_page ?? 1, total: 0, current_page: Number(params?.page) || 1 };
                  }
                  return data;
                });
              },
              dataLoader: true,
              dataLoaderLoading: t('Common.LoadingData'),
              dataLoaderError: t('Common.LoadFailed'),
              placeholder: t('MeasureGroupList.Empty'),
              height: 'calc(100vh - 193px)',
              progressiveLoad: 'scroll',
              progressiveLoadScrollMargin: 80,
              progressiveLoadDelay: 200,
              paginationSize: pageSizePc,
              filterMode: 'remote',
              sortMode: 'remote',
              pagination: false,
              ajaxRequesting: () => {
                if (tableBuiltRef.current && !isRestoringRef.current) captureHScroll();
                const table = getTable();
                if (table?.initialized) table.alert?.(t('Common.LoadingData'));
                return true;
              },
              ajaxResponse: (url, params, response) => {
                getTable()?.clearAlert?.();
                if (!response || typeof response !== 'object') return response;
                const data = enrichMeasureGroupRows(response.data || [], mask.measureType);
                // Ensure technical key matches helper even if BE omits order_number edge cases
                data.forEach((row) => {
                  if (row && !row.group_key) row.group_key = measureGroupKey(mask.measureType, row.order_number);
                });
                const next = { ...response, data };
                lastResponseRef.current = next;
                restoreHScroll();
                return next;
              },
              ajaxError: () => {
                getTable()?.clearAlert?.();
              },
              scrollToColumnIfVisible: false,
            }}
          />
        </div>
      </div>
      <Dialog header={t('Common.SearchInList')} visible={visibleSearch} style={{ width: '40vw' }} onHide={() => setVisibleSearch(false)}>
        <div className="flex flex-columns">
          <input
            value={searchValue}
            onChange={(e) => setSearchValue(e.target.value)}
            type="text"
            className="block w-full px-4 py-2 text-grey-700 bg-white border rounded-md focus:border-gray-400 focus:ring-gray-300 focus:outline-none focus:ring focus:ring-opacity-40"
          />
          <button type="button" onClick={() => searchTable()} className="w-16 ml-8 px-4 py-2 tracking-wide text-white transition-colors duration-200 transform bg-zinc-900 rounded-md hover:bg-zinc-700 focus:outline-none focus:bg-zinc-600"><SearchIcon /></button>
        </div>
      </Dialog>
      <Dialog header={t('MeasureGroupList.Search.ByDate')} visible={visibleCalendar} style={{ width: '40vw' }} onHide={() => setVisibleCalendar(false)}>
        <div className="flex flex-columns justify-between">
          <label>
            {t('MeasureGroupList.Search.From')}
            <input value={fromDate} onChange={(e) => setFromDate(e.target.value)} type="date" className="block w-full px-1 py-1 mr-2 text-grey-700 bg-white border rounded-md focus:border-gray-400 focus:ring-gray-300 focus:outline-none focus:ring focus:ring-opacity-40" />
          </label>
          <label>
            {t('MeasureGroupList.Search.To')}
            <input value={toDate} onChange={(e) => setToDate(e.target.value)} type="date" className="block w-full px-1 py-1 text-grey-700 bg-white border rounded-md focus:border-gray-400 focus:ring-gray-300 focus:outline-none focus:ring focus:ring-opacity-40" />
          </label>
          <button type="button" onClick={() => searchByDate()} className="w-16 ml-8 px-4 py-2 tracking-wide text-white transition-colors duration-200 transform bg-zinc-900 rounded-md hover:bg-zinc-700 focus:outline-none focus:bg-zinc-600"><SearchIcon /></button>
        </div>
      </Dialog>
      <Dialog header={t('Common.SetMask')} visible={maskVisible} style={{ width: '70vw' }} onHide={() => setMaskVisible(false)}>
        <div className="flex flex-col">
          <button type="button" onClick={() => { changeMarkings(!markUnmark); setMarkUnmarked(!markUnmark); }} className="m-1 px-4 py-2 text-center tracking-wide text-white transition-colors duration-200 transform bg-zinc-700 rounded-md hover:bg-zinc-500">{markUnmark ? t('Common.DeselectAll') : t('Common.SelectAll')}</button>
          {columns.map((item, index) => {
            if (!item?.field || !Object.prototype.hasOwnProperty.call(DEFAULT_COLUMNS_VISIBLE, item.field)) return null;
            return (
              <div key={item.field ?? index} className="flex flex-row border border-black rounded-md p-2 m-1">
                <Checkbox onChange={(e) => setMaskColumn(item.field, e.checked)} checked={!!columnsVisible[item.field]} />
                <span className="ml-2">{item.title}</span>
              </div>
            );
          })}
          <button type="button" onClick={() => { setUserMaskFilter(mask.pageListKey, 'set_columns', columnsVisible, null, null); setMaskVisible(false); }} className="m-1 px-4 py-2 text-center tracking-wide text-white transition-colors duration-200 transform bg-zinc-900 rounded-md hover:bg-zinc-700 focus:outline-none focus:bg-zinc-600">{t('Common.Save')}</button>
        </div>
      </Dialog>
      <PrintOptionsDialog
        visible={printChoiceVisible}
        onHide={() => setPrintChoiceVisible(false)}
        orientation={printOrientationChoice}
        onOrientationChange={setPrintOrientationChoice}
        layout={printLayoutChoice}
        onLayoutChange={setPrintLayoutChoice}
        onConfirm={runPrint}
      />
    </div>
  );
}
